import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ReportDefinition, ReportResult, ValueFormat } from '@goyone/shared';
import { api } from '../lib/api';
import { exportReport, printReportPath } from '../lib/reportExport';
import { Chart, formatValue } from './Chart';
import { DocPreview } from './DocPreview';
import { Menu } from './Menu';
import { ErrorState, Stat } from './ui';
import { useToast } from './Toast';

/**
 * بطاقةُ تقرير — الوحدةُ التي تُبنى منها اللوحات.
 *
 * تعريفٌ واحد يمرّ على محرّك التقارير نفسه الذي يخدم الباني، فالرقمُ في
 * اللوحة هو الرقمُ في الباني. وفي رأسها قائمةٌ: افتحه في الباني (لتعديل
 * تعريفه أو نشره)، وصدّره إكسل أو CSV، أو اطبعه PDF.
 */
export interface ReportCardProps {
  title: string;
  hint?: string;
  definition: ReportDefinition;
  /** معرّفُ التقرير المحفوظ — يفتح الباني عليه بدل تعريفٍ عابر. */
  savedId?: string;
  /** يظهر داخل «قسمٍ» في نمطٍ يُحرَّر — فلا رأسَ بطاقةٍ مكرّرًا. */
  compact?: boolean;
}

export function ReportCard({ title, hint, definition, savedId, compact }: ReportCardProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [printing, setPrinting] = useState(false);

  const result = useQuery({
    queryKey: ['reports', 'run', definition],
    queryFn: () => api<ReportResult>('/reports/run', { method: 'POST', body: definition }),
    staleTime: 60_000,
  });

  const download = async (format: 'xlsx' | 'csv') => {
    try {
      await exportReport(definition, title, format);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'تعذّر التصدير');
    }
  };

  const data = result.data;
  const format = (data?.format ?? 'NUMBER') as ValueFormat;

  return (
    <div className={`card report-card${compact ? ' is-compact' : ''}`}>
      <div className="card-head">
        <div style={{ minWidth: 0 }}>
          <h2 className="card-title">{title}</h2>
          {hint && <p className="tiny mute">{hint}</p>}
        </div>
        <Menu
          label="خيارات التقرير"
          actions={[
            {
              key: 'edit',
              label: savedId ? 'تعديل التقرير في الباني' : 'افتحه في الباني',
              icon: 'layers',
              onSelect: () =>
                navigate(
                  savedId
                    ? `/reports/builder?id=${savedId}`
                    : `/reports/builder?def=${encodeURIComponent(JSON.stringify(definition))}`,
                ),
            },
            {
              key: 'xlsx',
              label: 'تصدير إكسل',
              icon: 'download',
              separated: true,
              onSelect: () => void download('xlsx'),
            },
            {
              key: 'csv',
              label: 'تصدير CSV',
              icon: 'download',
              onSelect: () => void download('csv'),
            },
            {
              key: 'pdf',
              label: 'طباعة / PDF',
              icon: 'clipboard',
              onSelect: () => setPrinting(true),
            },
          ]}
        />
      </div>

      <div className="card-body">
        {result.isPending && <div className="skeleton" style={{ height: 160 }} />}
        {result.isError && (
          <ErrorState error={result.error} onRetry={() => void result.refetch()} />
        )}

        {data && data.rows.length === 0 && <p className="mute">لا بيانات في هذه المدة.</p>}

        {data && data.rows.length > 0 && (
          <>
            <div className="stats mb-4">
              <Stat
                label={data.measureLabel}
                value={<span className="num">{formatValue(data.total, format)}</span>}
              />
              <Stat label="عدد السجلات" value={data.records} />
            </div>

            {definition.chart !== 'TABLE' && (
              <Chart kind={definition.chart} rows={data.rows} format={format} />
            )}

            {definition.chart === 'TABLE' && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{data.dimensionLabel}</th>
                      <th style={{ width: 140 }}>{data.measureLabel}</th>
                      <th style={{ width: 80 }}>النسبة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.key}>
                        <td className="cell-strong">{r.label}</td>
                        <td className="num">{formatValue(r.value, format)}</td>
                        <td className="num cell-mute">{r.share}٪</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {printing && (
        <DocPreview
          path={printReportPath(definition, title)}
          title={title}
          onClose={() => setPrinting(false)}
        />
      )}
    </div>
  );
}
